import { UserInfoFormValues } from "@/components/HolderForm/UserInfoFormValues.ts";
import { RatePlanMerged } from "@/helpers/interfaces/RatePlanMerged.ts";
import {
  MandatoryExtra,
  ParcelObjectType,
  PaymentDetailsInput,
  RateGroup,
  ReservationOfferAccommodation,
} from "@/helpers/types/types.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { PaymentResultType } from "@/stores/EngineStore/enums/PaymentResultType.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { addCartItemHelper } from "@/stores/EngineStore/helpers/addCartItemHelper.ts";
import { addFullCartAccommodationItemsHelper } from "@/stores/EngineStore/helpers/addFullCartAccommodationItemsHelper.ts";
import { removeCartItemHelper } from "@/stores/EngineStore/helpers/removeCartItemHelper.ts";
import { resetEngineStoreHelper } from "@/stores/EngineStore/helpers/resetEngineStoreHelper.ts";
import { setAdultsAgeHelper } from "@/stores/EngineStore/helpers/setAdultsAgeHelper.ts";
import { setAdultsHelper } from "@/stores/EngineStore/helpers/setAdultsHelper.ts";
import { setChildrenAgeHelper } from "@/stores/EngineStore/helpers/setChildrenAgeHelper.ts";
import { setChildrenHelper } from "@/stores/EngineStore/helpers/setChildrenHelper.ts";
import { setDateRangeHelper } from "@/stores/EngineStore/helpers/setDateRangeHelper.ts";
import { setMandatoryExtrasHelper } from "@/stores/EngineStore/helpers/setMandatoryExtrasHelper.ts";
import {
  setParcelObjectAdditionalTentDimensionsHelper,
  setParcelObjectDataHelper,
  setParcelObjectDimensionsHelper,
  setParcelObjectHasMoverHelper,
} from "@/stores/EngineStore/helpers/setParcelObjectDataHelper.ts";
import { updateOptionsHelper } from "@/stores/EngineStore/helpers/updateOptionsHelper.ts";
import { CalculatedCart } from "@/stores/EngineStore/interfaces/CalculatedCart.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import { ExtendedOption } from "@/stores/EngineStore/interfaces/ExtendedOption.ts";
import { RootAttributes } from "@/stores/EngineStore/interfaces/RootAttributes.ts";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

export interface EngineStoreInitialData {
  // Global state
  rootAttributes: RootAttributes;
  page: AppPage;
  reservationHolderData: UserInfoFormValues | null;
  // encryptedData
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  paymentData: PaymentDetailsInput | null;
  paymentResult: PaymentResultType | null;
  cardType?: string;
  cardLastDigits?: string;
  cardExpireDate?: string;
  defaultDateRange: [Date | null, Date | null];
  defaultAdults: number;
  acceptedGDPR: boolean;
  defaultRateGroups: string[];
  isGDPRHighlighted: boolean;
  accommodationTypeId?: string;
  // Set to true after successful reservation/purchase
  isReserved: boolean;
  reservationOfferId?: string;

  // Cart state
  cartAccommodationItems: CartAccommodationItem[];
  calculatedCartData?: CalculatedCart;
}
export interface EngineStoreData extends EngineStoreInitialData {
  // Global state setters
  setRootAttributes: (attrs: RootAttributes) => void;
  setPage: (newPage: AppPage) => void;
  setReservationHolderData: (reservationHolderData: UserInfoFormValues | null) => void;
  setPaymentData: (paymentData?: Partial<PaymentDetailsInput>) => void;
  setPaymentResult: (paymentResult: PaymentResultType) => void;
  setDefaultDateRange: (defaultDateRange: [Date | null, Date | null]) => void;
  setDefaultAdults: (defaultAdults: number) => void;
  setAcceptedGDPR: (acceptedGDPR: boolean) => void;
  setDefaultRateGroups: (defaultRateGroups: string[]) => void;
  setIsGDPRHighlighted: (isGDPRHighlighted: boolean) => void;
  setAccommodationTypeId: (categoryId?: string) => void;
  setIsReserved: (isReserved: boolean) => void;
  setCardInfo: (cardType?: string, cardLastDigits?: string, cardExpireDate?: string) => void;
  setReservationOfferId: (reservationOfferId?: string) => void;

  // Cart setters
  setCalculatedCartData: (cart?: CalculatedCart) => void;
  setCartItemDateRange: (newDateRange: [Date | null, Date | null], itemId: string) => void;
  addCartItem: (rate: RatePlanMerged) => void;
  removeCartItem: (idToRemove: string) => void;
  setCartItemAdults: (num: number, itemId: string) => void;
  setCartItemAdultAge: (id: string, age: number, itemId: string) => void;
  setCartItemChild: (num: number, itemId: string) => void;
  setCartItemChildAge: (id: string, age: number, itemId: string) => void;
  updateCartItemOptions: (option: ExtendedOption, itemId: string) => void;
  setCartItemMandatoryExtras: (extras: MandatoryExtra[], itemId: string) => void;
  setCartItemParcelObjectType: (type: ParcelObjectType, itemId: string) => void;
  setCartItemParcelObjectDimensions: (dimensions: string, itemId: string) => void;
  setCartItemParcelObjectAdditionalTentDimensions: (dimensions: string, itemId: string) => void;
  setCartItemParcelObjectHasMover: (hasMover: boolean | undefined, itemId: string) => void;
  setMockCartAccommodationItems: (data: CartAccommodationItem[]) => void;

  // State setters from reservation/booking offer
  setFullCartAccommodationItems: (data: ReservationOfferAccommodation[]) => void;

  // State re-setters
  resetCartItems: () => void;
  resetAll: (skipDateRangeReset?: boolean) => void;
  //________________________________________________________________________________________

  // API state
  availableRateGroups: RateGroup[];
  // API state setters
  setAvailableRateGroups: (rateGroups: RateGroup[]) => void;
  // API state re-setters

  // Overrides
  inquiryOverride: boolean;
  setInquiryOverride: (override: boolean) => void;
}

/**
 *** EngineStore *** - global store for the Booking Engine
 * @page - Current application page, used as router to switch pages/screens
 * @default__ - Default properties used for initial state of the app
 * @calculatedCartTotal - total amount in the cart
 * @cartAccommodationItems - Selected Accommodations for the reservation
 * @reservationHolderData - Information about the user who will potentially complete the reservation
 * @rootAttributes - Information about the data atributes at the root (used for embedds)
 */

export const useEngineStore = create<EngineStoreData>()(
  devtools(
    persist(
      (set) => ({
        // FE state
        rootAttributes: {
          [RootAttribute.HideLanguagePicker]: undefined,
          [RootAttribute.ApiUrl]: undefined,
          [RootAttribute.HideParcelVehiclePicker]: undefined,
          [RootAttribute.ExcludeDates]: false,
        },
        page: AppPage.AccommodationPicking,
        reservationHolderData: null,
        paymentData: null,
        paymentResult: null,
        defaultDateRange: [null, null],
        defaultRateGroups: [],
        defaultAdults: 0,
        acceptedGDPR: false,
        isGDPRHighlighted: false,
        accommodationTypeId: undefined,
        isReserved: false,
        reservationOfferId: undefined,

        // FE state setters
        setRootAttributes: (rootAttributes) => set({ rootAttributes }),
        setPage: (page) => set({ page }),
        setReservationHolderData: (reservationHolderData) => set({ reservationHolderData }),
        setPaymentData: (paymentData) =>
          set((state) => ({
            ...state,
            paymentData: paymentData
              ? ({ ...state.paymentData, ...paymentData } as PaymentDetailsInput)
              : null,
          })),
        setPaymentResult: (paymentResult) => {
          set({ paymentResult, page: AppPage.PaymentResult });
        },
        setDefaultAdults: (defaultAdults) => set({ defaultAdults }),
        setDefaultDateRange: (defaultDateRange) => set({ defaultDateRange }),
        setDefaultRateGroups: (defaultRateGroups) => set({ defaultRateGroups }),
        setAcceptedGDPR: (acceptedGDPR) => set({ acceptedGDPR }),
        setIsGDPRHighlighted: (isGDPRHighlighted) => set({ isGDPRHighlighted }),
        setAccommodationTypeId: (accommodationTypeId) => set({ accommodationTypeId }),
        setIsReserved: (isReserved) => set({ isReserved }),
        setCardInfo: (cardType, cardLastDigits, cardExpireDate) =>
          set({ cardType, cardLastDigits, cardExpireDate }),
        setReservationOfferId: (reservationOfferId) => set({ reservationOfferId }),

        // Cart state
        cartAccommodationItems: [],
        calculatedCartData: undefined,

        // Cart state setters
        setCalculatedCartData: (cart) => set({ calculatedCartData: cart }),
        setCartItemDateRange: (dateRange, itemId) =>
          set((state) => setDateRangeHelper(state, itemId, dateRange)),
        addCartItem: (ratePlan) => set((state) => addCartItemHelper(state, ratePlan)),
        removeCartItem: (idToRemove) => set((state) => removeCartItemHelper(state, idToRemove)),
        setCartItemMandatoryExtras: (extra, itemId) =>
          set((state) => setMandatoryExtrasHelper(state, itemId, extra)),
        setCartItemAdults: (count, itemId) => set((state) => setAdultsHelper(state, count, itemId)),
        setCartItemAdultAge: (id, age, itemId) => set((state) => setAdultsAgeHelper(state, id, age, itemId)),
        setCartItemChild: (count, itemId) => set((state) => setChildrenHelper(state, count, itemId)),
        setCartItemChildAge: (id, age, itemId) =>
          set((state) => setChildrenAgeHelper(state, id, age, itemId)),
        updateCartItemOptions: (newOption, itemId) =>
          set((state) => updateOptionsHelper(state, newOption, itemId)),
        setCartItemParcelObjectType: (type, itemId) =>
          set((state) => setParcelObjectDataHelper(state, itemId, type)),
        setCartItemParcelObjectDimensions: (dimensions, itemId) =>
          set((state) => setParcelObjectDimensionsHelper(state, itemId, dimensions)),
        setCartItemParcelObjectAdditionalTentDimensions: (dimensions, itemId) =>
          set((state) => setParcelObjectAdditionalTentDimensionsHelper(state, itemId, dimensions)),
        setCartItemParcelObjectHasMover: (hasMover, itemId) =>
          set((state) => setParcelObjectHasMoverHelper(state, itemId, hasMover)),
        setMockCartAccommodationItems: (data) => set({ cartAccommodationItems: data }),

        // State setters from reservation/booking offer
        setFullCartAccommodationItems: (data) =>
          set((state) => addFullCartAccommodationItemsHelper(state, data)),

        // FE state re-setters
        resetCartItems: () => set({ cartAccommodationItems: [] }),
        resetAll: (skipDateRangeReset) => set(resetEngineStoreHelper(!!skipDateRangeReset)),
        //________________________________________________________________________________________

        // API state
        availableRateGroups: [],
        // API state setters
        setAvailableRateGroups: (rateGroups) => set({ availableRateGroups: rateGroups }),
        // API state re-setters

        // Overrides
        setInquiryOverride: (override) => set({ inquiryOverride: override }),
        inquiryOverride: false,
      }),
      {
        name: "bookingEngineStore",
        version: 1,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) =>
          Object.fromEntries(Object.entries(state).filter(([key]) => !nonPersistedKeys.includes(key))),
      },
    ),
  ),
);

const nonPersistedKeys = [
  "paymentResult",
  "paymentData",
  "cardType",
  "cardLastDigits",
  "cardExpireDate",
  "availableRateGroups",
  "inquiryOverride",
];
